
import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import { Page } from '../types';
import { ShieldCheckIcon } from '../assets/icons';

interface CheckoutPageProps {
    onNavigate: (page: Page) => void;
}

const CheckoutPage: React.FC<CheckoutPageProps> = ({ onNavigate }) => {
    const { cartItems, totalAmount, clearCart } = useCart();
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        cr: '',
        address: ''
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setStep(2);
        // Here you would typically send the data to a backend
        setTimeout(() => {
            clearCart();
            setStep(3);
        }, 1500);
    };

    if (cartItems.length === 0 && step === 1) {
        return (
            <div className="bg-gray-900 min-h-[calc(100vh-200px)] flex flex-col items-center justify-center p-6 text-center">
                <h2 className="text-2xl font-bold text-white mb-4">Seu carrinho está vazio.</h2>
                <button onClick={() => onNavigate('marketplace')} className="text-arsenal-red hover:underline font-bold">
                    Voltar ao Marketplace
                </button>
            </div>
        );
    }

    return (
        <div className="bg-gray-900 py-12 min-h-screen">
            <div className="container mx-auto px-6 max-w-4xl">
                <button
                    onClick={() => onNavigate('marketplace')}
                    className="mb-8 text-gray-400 hover:text-white flex items-center gap-2 transition-colors"
                >
                    &larr; Voltar
                </button>

                <h1 className="text-3xl md:text-4xl font-black text-white mb-8">Solicitação de Orçamento / Reserva</h1>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Order Summary */}
                    <div className="lg:col-span-1 order-2 lg:order-1">
                        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 top-24 sticky">
                            <h3 className="text-lg font-bold text-white mb-4">Resumo do Pedido</h3>
                            <ul className="space-y-4 mb-6">
                                {cartItems.map(item => (
                                    <li key={item.id} className="flex gap-3">
                                        <div className="w-12 h-12 bg-gray-900 rounded-lg overflow-hidden flex-shrink-0">
                                            <img src={item.imageUrls[0]} alt={item.name} className="w-full h-full object-cover" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-xs text-white font-bold line-clamp-1">{item.name}</p>
                                            <p className="text-xs text-gray-400">{item.quantity}x {item.price}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            <div className="border-t border-gray-700 pt-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-400 text-sm">Total Estimado</span>
                                    <span className="text-xl font-black text-white">
                                        R$ {totalAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Form Area */}
                    <div className="lg:col-span-2 order-1 lg:order-2">
                        {step === 1 && (
                            <div className="bg-gray-800/50 rounded-2xl p-6 md:p-8 border border-gray-700">
                                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                                    <UserIcon className="h-5 w-5 text-arsenal-red" />
                                    Seus Dados
                                </h3>
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Nome Completo</label>
                                            <input
                                                required
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-arsenal-red focus:ring-1 focus:ring-arsenal-red focus:outline-none transition-colors"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Email</label>
                                            <input
                                                required
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-arsenal-red focus:ring-1 focus:ring-arsenal-red focus:outline-none transition-colors"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Telefone / WhatsApp</label>
                                            <input
                                                required
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-arsenal-red focus:ring-1 focus:ring-arsenal-red focus:outline-none transition-colors"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">CR (Opcional)</label>
                                            <input
                                                type="text"
                                                name="cr"
                                                value={formData.cr}
                                                onChange={handleInputChange}
                                                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-arsenal-red focus:ring-1 focus:ring-arsenal-red focus:outline-none transition-colors"
                                                placeholder="Para agilizar a análise"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Endereço / Cidade</label>
                                        <input
                                            required
                                            type="text"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleInputChange}
                                            className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-arsenal-red focus:ring-1 focus:ring-arsenal-red focus:outline-none transition-colors"
                                        />
                                    </div>

                                    <div className="pt-4">
                                        <button
                                            type="submit"
                                            className="w-full bg-arsenal-red hover:bg-red-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-red-900/30 transition-all hover:-translate-y-1 flex items-center justify-center gap-2"
                                        >
                                            <ShieldCheckIcon className="h-5 w-5" />
                                            Enviar Solicitação
                                        </button>
                                        <p className="text-center text-xs text-gray-500 mt-3">
                                            A equipe do vendedor entrará em contato para verificar documentos e finalizar a venda.
                                        </p>
                                    </div>
                                </form>
                            </div>
                        )}

                        {step === 2 && (
                            <div className="bg-gray-800/50 rounded-2xl p-12 border border-gray-700 flex flex-col items-center justify-center text-center h-full min-h-[400px]">
                                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-arsenal-red mb-6"></div>
                                <h3 className="text-xl font-bold text-white">Processando sua solicitação...</h3>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="bg-gray-800/50 rounded-2xl p-12 border border-gray-700 flex flex-col items-center justify-center text-center h-full min-h-[400px]">
                                <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-6">
                                    <ShieldCheckIcon className="h-10 w-10 text-green-500" />
                                </div>
                                <h3 className="text-2xl font-black text-white mb-2">Solicitação Enviada!</h3>
                                <p className="text-gray-400 mb-8 max-w-md">
                                    Recebemos seu pedido de orçamento. O vendedor entrará em contato pelo WhatsApp/Email fornecido em breve para prosseguir com a documentação.
                                </p>
                                <button
                                    onClick={() => onNavigate('marketplace')}
                                    className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-8 rounded-xl transition-colors"
                                >
                                    Voltar ao Marketplace
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Helper Icon component (reusing from elsewhere ideally, but putting here to avoid import errors if not exported)
const UserIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
    </svg>
);

export default CheckoutPage;
